import { FloorPlanFilterListType } from '../../components/floor-plan-filter/floor-plan-filter-list-type.type';
import { FilterTile } from '../../components/floor-plan-map/floor-plan-map.interface';
import { IMeetingPlacePageFloorPlan } from '../../queries/fragments/meetingPlacePageFloorPlan';
import { IMeetingPlacePageServiceItem } from '../../queries/fragments/meetingPlacePageServiceItem';
import { TTenantTypes } from '../../queries/fragments/meetingPlacePageTenantList';
import { FloorPlanCategoriesData } from '../../templates/meeting-place/page/floorplan/meeting-place-page-floorplan';
import { generateCorrectSlugForPage } from '../generate-correct-slug';

export interface CategoryCount {
  [category: string]: number;
}

type Lists = {
  [key in FloorPlanFilterListType]?:
    | TTenantTypes[]
    | IMeetingPlacePageServiceItem[];
};

const isNewServiceDesignEnabled =
  process.env.GATSBY_SHOW_NEW_SERVICE_DESIGN === 'true' ||
  process.env.SHOW_NEW_SERVICE_DESIGN === 'true';

export const getInitialFilterTiles = (
  floorPlanCategoriesData: FloorPlanCategoriesData,
  lists: Lists,
  floorPlanPage: IMeetingPlacePageFloorPlan,
) => {
  const initialFilterTiles: FilterTile[] = [
    {
      title: floorPlanPage?.shopCategoryName,
      type: 'shop',
      images: { coverImage: floorPlanCategoriesData?.shop?.image },
      floorPlanIDs: [],
    },
    {
      title: floorPlanPage?.foodBeverageCatergoryName,
      type: 'fb',
      images: { coverImage: floorPlanCategoriesData?.fb?.image },
      floorPlanIDs: [],
    },
    {
      title: floorPlanPage?.entertainmentCategoryName,
      type: 'entertainment',
      images: { coverImage: floorPlanCategoriesData?.entertainment?.image },
      floorPlanIDs: [],
    },
    {
      title: floorPlanPage?.servicesCategoryName,
      type: 'service',
      images: { coverImage: floorPlanCategoriesData?.service?.image },
      floorPlanIDs: [],
    },
  ];

  const filteredTiles = initialFilterTiles.filter((tile) => {
    const isIncluded = lists[tile.type];
    return isIncluded && isIncluded.length > 0;
  });

  filteredTiles.forEach((tile) => {
    const isIncluded = lists[tile.type];
    if (isIncluded) {
      let categories: CategoryCount;
      if (tile.type === 'service') {
        categories = getCategoryInformationForServices(
          isIncluded as IMeetingPlacePageServiceItem[],
        );
      } else {
        categories = getCategoryInformationForTenants(
          isIncluded as TTenantTypes[],
        );
      }
      if (Object.keys(categories).length === 0) {
        tile.shouldSkipCategories = true;
      }
    }
  });

  return filteredTiles;
};

export const filterTenantByCategory = (
  tenant: TTenantTypes,
  category: string,
): boolean => {
  const tenantCategories = getCategoryInformationForTenants([tenant]);
  return tenantCategories[category] !== undefined;
};

export const filterServiceByCategory = (
  service: IMeetingPlacePageServiceItem,
  category: string,
): boolean => {
  const shopCategories = getCategoryInformationForServices([service]);
  return shopCategories[category] !== undefined;
};

export const getAllTilesList = (
  shops?: TTenantTypes[],
  foodbeverage?: TTenantTypes[],
  entertainment?: TTenantTypes[],
  services?: IMeetingPlacePageServiceItem[],
): FilterTile[] => {
  const tiles: FilterTile[] = [];

  const tenants: TTenantTypes[] = [];
  if (shops) tenants.push(...shops);
  if (foodbeverage) tenants.push(...foodbeverage);
  if (entertainment) tenants.push(...entertainment);

  for (const tenant of tenants) {
    const contentfulId = tenant.id;

    const tenantSlug = tenant.slug
      ? tenant.slug
      : tenant.brand?.slug
      ? tenant.brand?.slug
      : '';

    const slug = generateCorrectSlugForPage({
      slug: tenantSlug,
      customizedSlug: tenant.customizedSlug,
      node_locale: tenant.node_locale,
    });

    const title = tenant.name
      ? tenant.name
      : tenant.brand?.name
      ? tenant.brand?.name
      : '';

    const logo = tenant.logo
      ? tenant.logo
      : tenant.brand?.logo
      ? tenant.brand?.logo
      : undefined;

    const coverImage = tenant.coverImage
      ? tenant.coverImage
      : tenant.brand?.coverImage
      ? tenant.brand?.coverImage
      : undefined;

    tiles.push({
      contentfulId,
      floorPlanIDs: tenant.floorPlanIDs.map((id) => id.trim()),
      categories: getCategoryInformationForTenants([tenant]),
      images: { logo, coverImage },
      slug,
      title,
      type: tenant.__typename as FloorPlanFilterListType,
    });
  }

  for (const service of services ?? []) {
    const title = service.name
      ? service.name
      : service.serviceType?.name
      ? service.serviceType?.name
      : '';

    const slug = generateCorrectSlugForPage({
      slug: isNewServiceDesignEnabled
        ? service.serviceType?.slug
        : service.slug,
      customizedSlug: isNewServiceDesignEnabled
        ? service.serviceType.customizedSlug
        : service.customizedSlug,
      node_locale: service.node_locale,
    });

    tiles.push({
      contentfulId: service.id,
      categories: getCategoryInformationForServices([service]),
      floorPlanIDs: service.floorPlanIDs.map((id) => id.trim()),
      images: {
        coverImage: service?.coverImage || service.serviceType?.coverImage,
        icon: service?.floorPlanIcon || service.serviceType?.floorPlanIcon,
      },
      slug,
      title,
      type: service.__typename as FloorPlanFilterListType,
    });
  }

  return tiles;
};

export const getCategoryInformationForServices = (
  list: IMeetingPlacePageServiceItem[] | undefined,
): CategoryCount => {
  const categoryCount: CategoryCount = {};

  if (!list) return categoryCount;
  const categoryList: string[] = list.map((service) =>
    isNewServiceDesignEnabled
      ? service?.serviceType?.name
      : service?.serviceType?.serviceCategory?.name,
  );

  for (const category of categoryList) {
    if (!categoryCount[category]) {
      categoryCount[category] = 1;
    } else {
      categoryCount[category] = categoryCount[category] + 1;
    }
  }
  return categoryCount;
};

export const getCategoryInformationForAll = (
  shops?: TTenantTypes[],
  foodbeverage?: TTenantTypes[],
  entertainment?: TTenantTypes[],
  services?: IMeetingPlacePageServiceItem[],
): FilterTile[] => {
  const categoryCount: CategoryCount = {};

  const shopCategories = getCategoryInformationForTenants(shops);
  const fbCategories = getCategoryInformationForTenants(foodbeverage);
  const entertainmentCategories =
    getCategoryInformationForTenants(entertainment);
  const serviceCategories = getCategoryInformationForServices(services);

  const merge = (input: CategoryCount): void => {
    for (const [key, value] of Object.entries(input)) {
      if (categoryCount[key] === undefined) {
        categoryCount[key] = value;
      } else {
        categoryCount[key] += value;
      }
    }
  };
  merge(shopCategories);
  merge(fbCategories);
  merge(entertainmentCategories);
  merge(serviceCategories);

  return [
    { categories: categoryCount, type: 'all', title: '', floorPlanIDs: [] },
  ];
};

export const getCategoryInformationForTenants = (
  list: TTenantTypes[] | undefined,
): CategoryCount => {
  const categoryCount: CategoryCount = {};

  if (!list) return categoryCount;

  const categoryList: string[] = list
    .map((shop) => {
      const tenantSubCategories = shop?.secondaryCategories
        ? shop.secondaryCategories.map((c: { name: string }) => c.name)
        : [];

      const brandSubCategories = shop?.brand?.secondaryCategories
        ? shop?.brand?.secondaryCategories.map((c: { name: string }) => c.name)
        : [];

      const primaryCategory =
        shop.primaryCategory?.name || shop.brand?.primaryCategory?.name;

      const secondaryCategories =
        tenantSubCategories || brandSubCategories || [];

      const categories = [
        ...new Set([primaryCategory, ...secondaryCategories]),
      ];

      return categories.filter((x) => !!x);
    })
    .flat();

  for (const category of categoryList) {
    if (!categoryCount[category]) {
      categoryCount[category] = 1;
    } else {
      categoryCount[category] = categoryCount[category] + 1;
    }
  }
  return categoryCount;
};

export const isServiceTile = (tileType: FloorPlanFilterListType) => {
  return tileType === 'ContentfulMeetingPlacePageServiceItem';
};

export const extractQueryParams = () => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);

  const kioskId = params.get('kioskId');
  const routePreference = params.get('routePreference');
  const destination = params.get('destination');

  return {
    kioskId: kioskId || '',
    routePreference: routePreference || '',
    destination: destination || '',
  };
};
